<template>
  <div class="page-container app-page">

    <el-card v-loading="loading" class="seckill-detail-card" shadow="never">
      <template #header>
        <div class="card-header">
          <div class="card-header-left">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            <span>秒杀活动详情</span>
          </div>
          <el-tag type="danger" v-if="activity && activity.remainingCount <= 0">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            已抢完
          </el-tag>
        </div>
      </template>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="!activity && !error && !loading" class="empty-message">
        未找到秒杀活动数据
      </div>

      <div v-if="activity && !error" class="seckill-detail">
        <div class="product-info">
          <div class="product-image">
            <el-image
              :src="activity.productImage || 'https://picsum.photos/600/400'"
              alt="商品图片"
              fit="cover"
            />
            <div class="image-badge" v-if="seckillProgress > 0">
              <span>已抢 {{ seckillProgress }}%</span>
            </div>
          </div>

          <div class="product-info-right">
            <h2 class="product-title">{{ activity.productName || '未知商品' }}</h2>

            <div class="price-info">
              <span class="original-price">原价 <del>¥{{ (activity.originalPrice || 0).toFixed(2) }}</del></span>
              <span class="seckill-price">秒杀价 ¥{{ (activity.seckillPrice || 0).toFixed(2) }}</span>
            </div>

            <div class="countdown-box">
              <div class="countdown-label">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                本场秒杀结束时间
              </div>
              <el-countdown
                :time="remainingTime"
                format="HH:mm:ss"
                class="countdown"
              />
            </div>

            <div class="stock-section">
              <div class="stock-header">
                <span class="stock-label">库存状态</span>
                <span class="stock-remaining" :class="{ low: (activity.remainingCount || 0) <= 10 }">{{ activity.remainingCount || 0 }}件剩余</span>
              </div>
              <div class="stock-bar-wrap">
                <div class="stock-bar">
                  <div class="stock-fill" :style="{ width: seckillProgress + '%' }"></div>
                </div>
                <div class="stock-sold">
                  已抢 {{ (activity.totalCount || 0) - (activity.remainingCount || 0) }} / 总库存 {{ activity.totalCount || 0 }}
                </div>
              </div>
            </div>

            <div class="action-box">
              <el-button
                type="primary"
                size="large"
                :disabled="seckillDisabled"
                :loading="seckillLoading"
                @click="joinSeckill"
                class="seckill-btn"
              >
                <template v-if="!seckillLoading">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                  {{ seckillDisabled ? (activity.remainingCount <= 0 ? '已抢完' : '秒杀未开始') : '立即秒杀' }}
                </template>
              </el-button>
            </div>
          </div>
        </div>

        <div class="rule-box">
          <div class="rule-header">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <h3>秒杀规则</h3>
          </div>
          <ul class="rule-list">
            <li><span class="rule-dot"></span> 本场秒杀时间: {{ formatDate(activity.startTime) }} 至 {{ formatDate(activity.endTime) }}</li>
            <li><span class="rule-dot"></span> 每个用户限购1件，重复购买无效</li>
            <li><span class="rule-dot"></span> 秒杀成功后，请在15分钟内完成支付，否则订单将自动取消</li>
            <li><span class="rule-dot"></span> 秒杀商品不支持7天无理由退货，请谨慎购买</li>
            <li><span class="rule-dot"></span> 如有疑问，请联系客服: 400-123-4567</li>
          </ul>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
// 脚本逻辑完全不变，仅修改样式
import { ref, onMounted, computed, watch } from 'vue'
import { ElMessage, ElCountdown, ElImage, ElTag, ElButton, ElCard } from 'element-plus'
import seckillApi from '@/api/seckill'
import { useRouter, useRoute } from 'vue-router';

const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const router = useRouter();
const route = useRoute();

const activity = ref(null)
const loading = ref(true)
const error = ref('')
const userId = ref(localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null)
const seckillDisabled = ref(true)
const seckillLoading = ref(false)
const seckillResult = ref(null)
const activityId = ref(Number(route.params.activityId) || 0)

const remainingTime = computed(() => {
  if (!activity.value) return 0
  const endTime = new Date(activity.value.endTime).getTime()
  const now = new Date().getTime()
  return endTime - now > 0 ? endTime - now : 0
})

const seckillProgress = computed(() => {
  if (!activity.value) return 0
  const total = activity.value.totalCount || 0
  const remaining = activity.value.remainingCount || 0
  return total > 0 ? Math.round((total - remaining) / total * 100) : 0
})

const formatDate = (dateString) => {
  if (!dateString) return '未知时间'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const checkActivityStatus = () => {
  if (!activity.value) return
  const now = new Date().getTime()
  const startTime = new Date(activity.value.startTime).getTime()
  const endTime = new Date(activity.value.endTime).getTime()
  seckillDisabled.value = !(now >= startTime && now <= endTime && (activity.value.remainingCount || 0) > 0)
}

const fetchSeckillDetail = async () => {
  try {
    loading.value = true
    await sleep(1000)
    const res = activityId.value
      ? await seckillApi.getSeckillActivityDetail(activityId.value)
      : await seckillApi.getCurrentSeckillActivities();

    if (res.data.code == '200' || res.data.code == 200 ) {
      activity.value = res.data.data;
      checkActivityStatus();
    } else {
      error.value = res.msg || res.message || '获取秒杀活动详情失败'
      ElMessage.error(error.value)
    }
  } catch (err) {
    console.error('请求错误详情:', err);
    error.value = '获取秒杀活动详情失败，请稍后重试'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

const joinSeckill = async () => {
  if (!userId.value) {
    ElMessage.error('请先登录')
    router.push('/login')
    return
  }

  if (!activity.value) {
    ElMessage.error('活动数据不存在')
    return
  }

  try {
    seckillLoading.value = true
    const seckillData = {
      userId: userId.value,
      items: [{
        productId: activity.value.productId,
        count: 1,
        price: activity.value.seckillPrice
      }],
      activityId: activity.value.id,
      isSeckill: true
    }

    const res = await seckillApi.doSeckill(seckillData)
    if (res.data.code == '200' || res.data.code == 200) {
      seckillResult.value = res.data
      ElMessage.success('秒杀成功，正在跳转到订单支付页面')
      router.push(`/order/detail/${res.data.data}`)
    } else {
      ElMessage.error(res.msg || res.message || '秒杀失败，请重试')
    }
  } catch (err) {
    ElMessage.error(err.message || '秒杀过程中发生错误')
  } finally {
    seckillLoading.value = false
  }
}

watch(remainingTime, () => {
  checkActivityStatus()
})

onMounted(() => {
  fetchSeckillDetail()
})
</script>

<style scoped>
.page-container {
  max-width: 1000px;
  margin: 0 auto;
}

.seckill-detail-card {
  margin-top: 0;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  overflow: hidden;
}

.seckill-detail-card :deep(.el-card__header) {
  padding: 16px 24px;
  border-bottom: 1px solid var(--app-border-light);
  background: var(--app-bg-soft);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--app-text-primary);
  font-size: 0.9375rem;
}

.card-header-left svg {
  color: var(--app-color-primary);
}

.el-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: var(--app-radius-full);
}

.error-message {
  color: #f56c6c;
  text-align: center;
  padding: 60px 20px;
  font-size: 0.9375rem;
}

.empty-message {
  color: var(--app-text-secondary);
  text-align: center;
  padding: 60px 20px;
  font-size: 0.9375rem;
}

.product-info {
  display: flex;
  gap: 32px;
  padding: 24px;
  flex-wrap: wrap;
}

.product-image {
  flex: 1;
  min-width: 320px;
  position: relative;
}

.product-image :deep(.el-image) {
  width: 100%;
  height: 360px;
  border-radius: var(--app-radius-lg);
}

.image-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(245, 108, 108, 0.9);
  color: #fff;
  padding: 4px 12px;
  border-radius: var(--app-radius-full);
  font-size: 0.8125rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.product-info-right {
  flex: 1.5;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--app-text-primary);
  line-height: 1.3;
}

.price-info {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(245, 108, 108, 0.08), rgba(255, 126, 126, 0.03));
  border-radius: var(--app-radius-lg);
  border: 1px solid rgba(245, 108, 108, 0.12);
}

.original-price {
  color: var(--app-text-muted);
  font-size: 0.9375rem;
}

.original-price del {
  margin-left: 4px;
}

.seckill-price {
  color: #f56c6c;
  font-size: 1.75rem;
  font-weight: 800;
}

.countdown-box {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-subtle);
}

.countdown-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--app-text-secondary);
}

.countdown-label svg {
  color: var(--app-color-primary);
}

.countdown :deep(.el-countdown__content) {
  font-size: 2rem;
  font-weight: 700;
  color: var(--app-color-primary);
  font-variant-numeric: tabular-nums;
  letter-spacing: 2px;
}

.stock-section {
  margin-bottom: 20px;
}

.stock-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.stock-label {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
  font-weight: 500;
}

.stock-remaining {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.stock-remaining.low {
  color: #f56c6c;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.stock-bar-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stock-bar {
  flex: 1;
  height: 8px;
  background: var(--app-bg-soft);
  border-radius: 4px;
  overflow: hidden;
}

.stock-fill {
  height: 100%;
  background: var(--app-gradient-primary);
  border-radius: 4px;
  transition: width 0.6s ease;
}

.stock-sold {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  white-space: nowrap;
}

.action-box {
  margin-top: 8px;
}

.seckill-btn {
  width: 100%;
  padding: 16px !important;
  font-size: 1.125rem !important;
  font-weight: 700 !important;
  border-radius: var(--app-radius-lg) !important;
  background: var(--app-gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35) !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.seckill-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.5) !important;
}

.seckill-btn:disabled {
  opacity: 0.5;
  box-shadow: none !important;
}

/* Rules */
.rule-box {
  padding: 20px 24px 24px;
  border-top: 1px solid var(--app-border-light);
  background: var(--app-bg-soft);
  border-radius: 0 0 var(--app-radius-xl) var(--app-radius-xl);
}

.rule-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.rule-header svg {
  color: var(--app-color-primary);
}

.rule-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.rule-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.rule-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: var(--app-text-secondary);
  font-size: 0.875rem;
  line-height: 1.6;
}

.rule-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--app-color-primary);
  flex-shrink: 0;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .product-info {
    flex-direction: column;
    padding: 16px;
  }

  .product-image :deep(.el-image) {
    height: 240px;
  }

  .product-title {
    font-size: 1.25rem;
  }

  .seckill-price {
    font-size: 1.5rem;
  }

  .countdown :deep(.el-countdown__content) {
    font-size: 1.5rem;
  }
}
</style>
